import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UserRole } from '../../redux/authSlice';
import {
  LayoutDashboard,
  Users,
  FileCheck,
  CreditCard,
  DollarSign,
  Zap,
  Search,
  User,
  UserCog,
  Trash2,
  Building2,
  FlaskConical,
  Wallet,
  ChevronRight,
  ShieldCheck,
} from 'lucide-react';

interface AdminSidebarProps {
  isOpen?: boolean;
  onNavigate?: () => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ isOpen = false, onNavigate }) => {
  const location = useLocation();

  const menuGroups = [
    {
      label: 'Overview',
      items: [
        { path: '/admin/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={17} />, requiredRole: 'admin' as UserRole },
      ],
    },
    {
      label: 'Approvals',
      items: [
        { path: '/admin/pending-registrations', label: 'Registrations', icon: <Users size={17} />, requiredRole: 'admin' as UserRole },
        { path: '/admin/document-approvals', label: 'Documents', icon: <FileCheck size={17} />, requiredRole: 'admin' as UserRole },
        { path: '/admin/payment-requests', label: 'Payment Requests', icon: <CreditCard size={17} />, requiredRole: 'admin' as UserRole },
      ],
    },
    {
      label: 'Payments',
      items: [
        { path: '/admin/admin-payments', label: 'All Payments', icon: <DollarSign size={17} />, requiredRole: 'admin' as UserRole },
        { path: '/admin/admin-action', label: 'Actions', icon: <Zap size={17} />, requiredRole: 'admin' as UserRole },
        { path: '/admin/admin-review', label: 'Review', icon: <Search size={17} />, requiredRole: 'admin' as UserRole },
      ],
    },
    {
      label: 'Users & Accounts',
      items: [
        { path: '/admin/profile', label: 'Profile & 2FA', icon: <User size={17} />, requiredRole: 'admin' as UserRole },
        { path: '/admin/user-management', label: 'User Management', icon: <UserCog size={17} />, requiredRole: 'admin' as UserRole },
        { path: '/admin/bank-account', label: 'Bank Accounts', icon: <Building2 size={17} />, requiredRole: 'admin' as UserRole },
        { path: '/admin/virtual-accounts', label: 'Virtual Accounts', icon: <Wallet size={17} />, requiredRole: 'admin' as UserRole },
        { path: '/admin/delete-unverified-users', label: 'Cleanup Users', icon: <Trash2 size={17} />, requiredRole: 'admin' as UserRole },
      ],
    },
    {
      label: 'Tools',
      items: [
        { path: '/admin/simulations', label: 'Simulations', icon: <FlaskConical size={17} />, requiredRole: 'admin' as UserRole },
      ],
    },
  ];

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <>
      {/* Sidebar Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 z-20 bg-fintech-text/30 backdrop-blur-sm lg:hidden"
          onClick={onNavigate}
        />
      )}

      <aside
        className={`
          enterprise-sidebar enterprise-sidebar--admin fixed top-0 left-0 h-full z-30 w-72 sm:w-64 flex flex-col
          transition-transform duration-300 ease-in-out shadow-2xl
          lg:relative lg:translate-x-0 lg:z-auto
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/20 to-black/20 pointer-events-none lg:hidden" />
        {/* Sidebar Header */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-slate-700">
          <div className="w-8 h-8 bg-fintech-secondary rounded-lg flex items-center justify-center">
            <ShieldCheck size={16} className="text-white" />
          </div>
          <div>
            <p className="text-white font-bold text-sm leading-tight">Kenluk Pay</p>
            <p className="text-slate-400 text-xs">Admin Console</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto scrollbar-hide">
          {menuGroups.map((group) => (
            <div key={group.label} className="mb-5">
              <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider px-3 mb-1.5">
                {group.label}
              </p>
              <ul className="flex flex-col gap-0.5">
                {group.items.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={onNavigate}
                      className={`
                        flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                        transition-all duration-150 no-underline group
                        ${isActive(item.path)
                          ? 'bg-fintech-secondary/20 text-fintech-secondary border border-fintech-secondary/30'
                          : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                        }
                      `}
                    >
                      <span className={`flex-shrink-0 ${isActive(item.path) ? 'text-fintech-secondary' : 'text-slate-500 group-hover:text-slate-300'}`}>
                        {item.icon}
                      </span>
                      <span className="flex-1 truncate font-semibold lg:font-medium">{item.label}</span>
                      {isActive(item.path) && (
                        <ChevronRight size={14} className="text-fintech-secondary/60" />
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Sidebar Footer */}
        <div className="px-4 py-4 border-t border-slate-700">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <div className="w-1.5 h-1.5 rounded-full bg-fintech-accent animate-pulse" />
            <span>Admin access active</span>
          </div>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;
